import React, { useState, useEffect, useRef } from "react";
import { FiAlignJustify } from "react-icons/fi";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { 
  MdCalendarToday,
  MdChevronLeft,
  MdChevronRight,
  MdClose
} from "react-icons/md";
import Select from "react-select";

const PRIMARY_OPTIONS = [
  { label: "Events", value: "events" },
  { label: "Posts", value: "posts" },
  { label: "Garages", value: "garages" },
];

const VENDOR_OPTIONS = [
  { label: "Vendor Categories", value: "categories" },
  { label: "Vendor Requests", value: "requests" },
  { label: "Vendor Products", value: "products" },
];

const selectStyles = {
  option: (styles) => ({
    ...styles,
    backgroundColor: "rgb(30 30 32 / 1) !important",
    color: "#ffffff",
    ":hover": {
      backgroundColor: "rgba(255, 255, 255, 0.30) !important",
      color: "#ffffff",
    },
  }),
  control: (styles, { isFocused }) => ({
    ...styles,
    backgroundColor: "rgb(12 14 28 / 1)",
    borderRadius: "9999px",
    borderColor: "rgba(255, 255, 255, 0.25)",
    boxShadow: "none",
    outline: "none",
    ...(isFocused
      ? {
          borderColor: "rgba(255, 255, 255, 0.35)",
        }
      : {}),
  }),
  singleValue: (styles) => ({
    ...styles,
    color: "#ffffff",
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: "rgb(12 14 28 / 1)",
    borderRadius: "12px",
    border: "1px solid #2A2A2A",
    marginTop: "35px",
    overflow: "hidden",
  }),
};

const Navbar = (props) => {
  const { onOpenSidenav, brandText } = props;
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDashboard = location.pathname === "/dashboard";
  const isExplore = location.pathname === "/explore";
  const isVendor = location.pathname === "/vendor";
  const [dateRange, setDateRange] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const calendarRef = useRef(null);

  // Explore dropdown state
  const tabParam = searchParams.get("tab");
  const getInitialExploreOption = React.useCallback(() => {
    if (tabParam === "garages") {
      return PRIMARY_OPTIONS.find(opt => opt.value === "garages") || PRIMARY_OPTIONS[0];
    } else if (tabParam === "posts") {
      return PRIMARY_OPTIONS.find(opt => opt.value === "posts") || PRIMARY_OPTIONS[0];
    }
    return PRIMARY_OPTIONS[0];
  }, [tabParam]);
  
  const [exploreOption, setExploreOption] = useState(() => {
    if (tabParam === "garages") {
      return PRIMARY_OPTIONS.find(opt => opt.value === "garages") || PRIMARY_OPTIONS[0];
    } else if (tabParam === "posts") {
      return PRIMARY_OPTIONS.find(opt => opt.value === "posts") || PRIMARY_OPTIONS[0];
    }
    return PRIMARY_OPTIONS[0];
  });

  // Update explore option when URL parameter changes
  useEffect(() => {
    if (isExplore) {
      const newOption = getInitialExploreOption();
      setExploreOption(newOption);
    }
  }, [tabParam, isExplore, getInitialExploreOption]);

  const handleExploreOptionChange = (option) => {
    if (option) {
      setExploreOption(option);
      const newSearchParams = new URLSearchParams(searchParams);
      if (option.value === "events") {
        newSearchParams.delete("tab");
      } else {
        newSearchParams.set("tab", option.value);
      }
      setSearchParams(newSearchParams, { replace: true });
    }
  };

  // Vendor dropdown state
  const vendorTabParam = searchParams.get("tab");
  const vendorStatusParam = searchParams.get("status");
  const getInitialVendorOption = React.useCallback(() => {
    if (vendorTabParam === "requests" || vendorStatusParam) {
      return VENDOR_OPTIONS.find(opt => opt.value === "requests") || VENDOR_OPTIONS[0];
    } else if (vendorTabParam === "products") {
      return VENDOR_OPTIONS.find(opt => opt.value === "products") || VENDOR_OPTIONS[0];
    } else if (vendorTabParam === "categories") {
      return VENDOR_OPTIONS.find(opt => opt.value === "categories") || VENDOR_OPTIONS[0];
    }
    return VENDOR_OPTIONS[0]; // Default to categories
  }, [vendorTabParam, vendorStatusParam]);

  const [vendorOption, setVendorOption] = useState(() => {
    if (vendorTabParam === "requests" || vendorStatusParam) {
      return VENDOR_OPTIONS.find(opt => opt.value === "requests") || VENDOR_OPTIONS[0];
    } else if (vendorTabParam === "products") {
      return VENDOR_OPTIONS.find(opt => opt.value === "products") || VENDOR_OPTIONS[0];
    } else if (vendorTabParam === "categories") {
      return VENDOR_OPTIONS.find(opt => opt.value === "categories") || VENDOR_OPTIONS[0];
    }
    return VENDOR_OPTIONS[0];
  });

  // Update vendor option when URL parameter changes
  useEffect(() => {
    if (isVendor) {
      const newOption = getInitialVendorOption();
      setVendorOption(newOption);
    }
  }, [vendorTabParam, vendorStatusParam, isVendor, getInitialVendorOption]);

  const handleVendorOptionChange = (option) => {
    if (option) {
      setVendorOption(option);
      const newSearchParams = new URLSearchParams(searchParams);
      // Clear status param when switching away from requests
      if (option.value !== "requests") {
        newSearchParams.delete("status");
      }
      // Set tab param based on selection
      if (option.value === "categories") {
        newSearchParams.delete("tab");
      } else {
        newSearchParams.set("tab", option.value);
      }
      setSearchParams(newSearchParams, { replace: true });
    }
  };

  // Load date range from localStorage only when on dashboard
  useEffect(() => {
    if (isDashboard) {
      const savedDateRange = localStorage.getItem("dashboard-date-range");
      const savedStartDate = localStorage.getItem("dashboard-start-date");
      const savedEndDate = localStorage.getItem("dashboard-end-date");
      
      if (savedDateRange) {
        setDateRange(savedDateRange);
      }
      if (savedStartDate) {
        const startDate = new Date(savedStartDate);
        startDate.setHours(0, 0, 0, 0);  
        setSelectedStartDate(startDate);
      }
      if (savedEndDate) {
        const endDate = new Date(savedEndDate);
        endDate.setHours(0, 0, 0, 0);
        setSelectedEndDate(endDate);
      }
    } else {
      // Clear state when not on dashboard
      setDateRange("");
      setSelectedStartDate(null);
      setSelectedEndDate(null);
    }
  }, [isDashboard]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close calendar and clear date selection when navigating away from Dashboard
  useEffect(() => {
    if (!isDashboard) {
      setShowCalendar(false);
      // Clear calendar state when navigating away from dashboard
      setDateRange("");
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      // Clear localStorage
      localStorage.removeItem("dashboard-date-range");
      localStorage.removeItem("dashboard-start-date");
      localStorage.removeItem("dashboard-end-date");
      // Trigger event to notify dashboard that dates are cleared
      window.dispatchEvent(new CustomEvent('dateRangeChanged', { 
        detail: { startDate: null, endDate: null } 
      }));
    }
  }, [isDashboard]);

  const months = [
    "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
    "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"
  ];

  const quickSelectOptions = [
    "Today",
    "Yesterday", 
    "Last 7 days",
    "Last 30 days",
    "This month",
    "Last month"
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      const prevMonth = new Date(year, month, 0);
      days.push({
        day: prevMonth.getDate() - startingDayOfWeek + i + 1,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonth.getDate() - startingDayOfWeek + i + 1)
      });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        isCurrentMonth: true,
        date: new Date(year, month, day)
      });
    }
    
    const remainingCells = 42 - days.length;
    for (let day = 1; day <= remainingCells; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        date: new Date(year, month + 1, day)
      });
    }
    
    return days;
  };

  const handleDateClick = (date) => {
    // Normalize date to start of day
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    
    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(normalizedDate);
      setSelectedEndDate(null);
      setDateRange("");
      localStorage.removeItem("dashboard-date-range");
      localStorage.removeItem("dashboard-start-date");
      localStorage.removeItem("dashboard-end-date");
    } else {
      // Normalize selected start date
      const normalizedStart = new Date(selectedStartDate);
      normalizedStart.setHours(0, 0, 0, 0);
      
      let start = normalizedStart;
      let end = normalizedDate;
      
      if (normalizedDate < normalizedStart) {
        start = normalizedDate;
        end = normalizedStart;
      }
      
      setSelectedStartDate(start);
      setSelectedEndDate(end);
      
      const formatDate = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const formattedRange = `${formatDate(start)} ~ ${formatDate(end)}`;
      setDateRange(formattedRange);
      
      // Save to localStorage
      localStorage.setItem("dashboard-date-range", formattedRange);
      localStorage.setItem("dashboard-start-date", start.toISOString());
      localStorage.setItem("dashboard-end-date", end.toISOString());
      
      // Trigger custom event for Dashboard to listen
      window.dispatchEvent(new CustomEvent('dateRangeChanged', { 
        detail: { startDate: start, endDate: end } 
      }));
      
      setShowCalendar(false);
    }
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate) return false;
    
    // Normalize dates to start of day for accurate comparison
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    
    const normalizedStart = new Date(selectedStartDate);
    normalizedStart.setHours(0, 0, 0, 0);
    
    if (!selectedEndDate) {
      return normalizedDate.getTime() === normalizedStart.getTime();
    }
    
    const normalizedEnd = new Date(selectedEndDate);
    normalizedEnd.setHours(0, 0, 0, 0);
    
    return normalizedDate >= normalizedStart && normalizedDate <= normalizedEnd;
  };

  const getQuickSelectDates = (option) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day
    let startDate, endDate;
    
    switch (option) {
      case "Today":
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case "Yesterday":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 1);
        endDate = new Date(today);
        endDate.setDate(today.getDate() - 1);
        break;
      case "Last 7 days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 6);
        endDate = new Date(today);
        break;
      case "Last 30 days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 29);
        endDate = new Date(today);
        break;
      case "This month":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        endDate.setHours(0, 0, 0, 0);
        break;
      case "Last month":
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        endDate.setHours(0, 0, 0, 0);
        break;
      default:
        return null;
    }
    
    // Ensure all dates are normalized to start of day
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    
    return { startDate, endDate };
  };

  const isQuickSelectSelected = (option) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    
    const optionDates = getQuickSelectDates(option);
    if (!optionDates) return false;
    
    const { startDate, endDate } = optionDates;
    
    // Compare dates (ignoring time)
    const normalizeDate = (date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };
    
    return (
      normalizeDate(selectedStartDate) === normalizeDate(startDate) &&
      normalizeDate(selectedEndDate) === normalizeDate(endDate)
    );
  };

  const handleQuickSelect = (option) => {
    const optionDates = getQuickSelectDates(option);
    if (!optionDates) return;
    
    const { startDate, endDate } = optionDates;
    
    // Ensure dates are normalized (already done in getQuickSelectDates, but double-check)
    const normalizedStart = new Date(startDate);
    normalizedStart.setHours(0, 0, 0, 0);
    const normalizedEnd = new Date(endDate);
    normalizedEnd.setHours(0, 0, 0, 0);
    
    setSelectedStartDate(normalizedStart);
    setSelectedEndDate(normalizedEnd);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const formattedRange = `${formatDate(normalizedStart)} ~ ${formatDate(normalizedEnd)}`;
    setDateRange(formattedRange);
    
    // Save to localStorage
    localStorage.setItem("dashboard-date-range", formattedRange);
    localStorage.setItem("dashboard-start-date", normalizedStart.toISOString());
    localStorage.setItem("dashboard-end-date", normalizedEnd.toISOString());
    
    // Trigger custom event for Dashboard to listen
    window.dispatchEvent(new CustomEvent('dateRangeChanged', { 
      detail: { startDate: normalizedStart, endDate: normalizedEnd } 
    }));
    
    setShowCalendar(false);
  };

  const handleClearDate = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setDateRange("");
    localStorage.removeItem("dashboard-date-range");
    localStorage.removeItem("dashboard-start-date");
    localStorage.removeItem("dashboard-end-date");
    
    // Trigger custom event for Dashboard to listen
    window.dispatchEvent(new CustomEvent('dateRangeChanged', { 
      detail: { startDate: null, endDate: null } 
    }));
  };

  return (
    <>
      <style>
        {`
          .navbar-scrollable {
            scrollbar-width: none; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
            box-shadow: none !important;
            border: none !important;
          }
          .navbar-scrollable::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }
          @media (max-width: 425px) {
            .navbar-scrollable {
              flex-wrap: nowrap !important;
            }
            .date-picker-container {
              margin-top: 0 !important;
              align-self: center !important;
              display: flex !important;
              align-items: center !important;
              flex-shrink: 1 !important;
              min-width: 0 !important;
              margin-right: -0.5rem !important;
            }
            .date-picker-input {
              width: 140px !important;
              min-width: 140px !important;
              font-size: 10px !important;
              padding-left: 0.5rem !important;
              padding-right: 1.75rem !important;
              height: 2rem !important;
            }
            .date-picker-container svg {
              width: 0.875rem !important;
              height: 0.875rem !important;
              right: 0.5rem !important;
            }
            .explore-dropdown-container {
              margin-top: -1rem !important;
            }
            .explore-dropdown-container .select__control {
              height: 1.75rem !important;
            }
            .vendor-dropdown-container {
              margin-top: 0.5rem !important;
              align-self: center !important;
              display: flex !important;
              align-items: center !important;
              flex-shrink: 1 !important;
              min-width: 0 !important;
              margin-right: -4rem !important;
            }
            .vendor-dropdown-container .select__control {
              width: 160px !important;
              min-width: 160px !important;
              font-size: 12px !important;
              height: 1.75rem !important;
            }
            .vendor-dropdown-container .select__single-value {
              font-size: 12px !important;
            }
            .navbar-brand-text {
              font-size: 1.5rem !important;
              white-space: normal !important;
              overflow: visible !important;
              margin-left: 0.25rem !important;
              flex-shrink: 1 !important;
              min-width: 0 !important;
              word-wrap: break-word !important;
              line-height: 1.2 !important;
            }
            .navbar-brand-text a {
              font-size: 1.5rem !important;
              margin-top:0.5rem !important;
              display: block !important;
              overflow: visible !important;
              white-space: normal !important;
              word-wrap: break-word !important;
              line-height: 1.2 !important;
            }
            .navbar-scrollable > div:first-child {
              flex-shrink: 1 !important;
              min-width: 0 !important;
              flex-basis: auto !important;
              max-width: 100% !important;
            }
          }
          @media (min-width: 1200px) {
            p.navbar-brand-text {
              margin-left: 0.1rem !important;
            }
            p.navbar-brand-text.dashboard-text {
              margin-left: 1rem !important;
                      }
         
        `}
      </style>
      <nav className="navbar-scrollable sticky top-0 z-40 flex flex-row flex-wrap items-center justify-between rounded-xl p-3 pt-5 min-h-[80px] backdrop-blur-none header shadow-none border-none" style={{ boxShadow: 'none', border: 'none' }}>
        <div className="ml-[6px] flex items-center gap-3">
          <span
            className="flex cursor-pointer text-xl text-gray-600 dark:text-white xl:hidden"
            onClick={onOpenSidenav}
          >
            <FiAlignJustify className="h-5 w-5" />
          </span>
          <p className={`shrink text-2xl xxs:text-xl xs:text-[29px] md:text-[33px] capitalize text-white ml-0 ${!isDashboard ? 'min-[1200px]:-ml-2' : ''} navbar-brand-text flex items-center -mt-1 ${isDashboard ? 'dashboard-text' : ''}`}>
            <Link to="#" className="font-bold capitalize hover:text-white">
              {brandText === "Boost Pricing"
                ? "Boosts & Ticket Pricing"
                : brandText}
            </Link>
          </p>
        </div>

        <div className="relative flex w-fit items-center justify-end md:justify-around rounded-full px-6 py-2 shadow-none border-none backdrop-blur-none md:flex-grow-0 md:gap-1 xl:gap-2 ml-auto md:ml-0" style={{ overflow: 'visible', boxShadow: 'none', border: 'none' }}>
          {/* Explore Dropdown - Only show on Explore page */}
          
          {isExplore && (
            <>
            <style>
              {`
                @media (min-width: 375px) and (max-width: 425px) {
                  .explore-dropdown-container {
                    width: 150px !important;
                    min-width: 150px !important;
                  }
                  .explore-dropdown-container .select__control {
                    width: 130px !important;
                    min-width: 120px !important;
                  }
                }
                @media (max-width: 374px) {
                  .explore-dropdown-container {
                    transform: translate(6rem, 0rem) !important;
                    width: 135px !important;
                    min-width: 135px !important;
                    margin-right: 4.5rem !important;
                  }
                  .explore-select-dropdown {
                    width: 135px !important;
                    min-width: 135px !important;
                  
                  }
                  .explore-select-dropdown .select__control {
                    width: 135px !important;
                    min-width: 135px !important;
                  }
                  .explore-select-dropdown .select__menu {
                    max-width: calc(100vw - 2rem) !important;
                    right: 0 !important;
                    margin-top: 40px !important;
                  }
                  .explore-select-dropdown .select__menu-portal {
                    max-width: calc(100vw - 2rem) !important;
                  }
                }
              `}
            </style>
            <div className="explore-dropdown-container flex h-full w-[180px] items-center justify-end -mt-4 translate-x-7">
              <Select
                className="h-4 w-[180px] !text-white"
                classNamePrefix="select"
                isSearchable={false}
                options={PRIMARY_OPTIONS}
                value={exploreOption}
                onChange={handleExploreOptionChange}
                styles={selectStyles}
              />
            </div>
          </>
        
          )}
          {/* Vendor Dropdown - Only show on Vendor page */}
          {isVendor && (
            <div className="relative mb-6 sm:mt-0 -mr-4 sm:ml-0 vendor-dropdown-container" style={{ overflow: 'visible', zIndex: 9999 }}>
              <Select
                className="h-4 w-[200px] sm:w-[220px] !text-white"
                classNamePrefix="select"
                isSearchable={false}
                options={VENDOR_OPTIONS}
                value={vendorOption}
                onChange={handleVendorOptionChange}
                styles={selectStyles}
              />
            </div>
          )}
          {/* Date Range Picker - Only show on Dashboard */}
          {isDashboard && (
          <div className="relative mt-0.5 sm:mt-0 -mr-1 sm:-ml-0 date-picker-container" ref={calendarRef} style={{ overflow: 'visible', zIndex: 9999 }}>
            <div className="relative">
              <input
                type="text"
                placeholder="YYYY-MM-DD ~ YYYY-MM-DD"
                value={dateRange}
                readOnly
                  onClick={() => setShowCalendar(!showCalendar)}
                className="date-picker-input w-52 sm:w-72 md:w-72  mt-0.5 h-10 sm:h-11  px-2 sm:px-4 pr-10 sm:pr-12 bg-navy-800 border border-white/10 focus:border-white/20 rounded-xl text-white placeholder-[#8E8E8E] text-[11px] sm:text-sm tracking-wide focus:outline-none cursor-pointer transition-colors"  
              />
              {dateRange ? (
                <MdClose 
                  className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-white cursor-pointer" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearDate();
                  }}
                />
              ) : (
                <MdCalendarToday 
                  className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-white cursor-pointer" 
                  onClick={() => setShowCalendar(!showCalendar)}
                />
              )}
            </div>
            
            {/* Calendar Popup */}
            {showCalendar && (
              <div className="absolute top-full right-0 mt-2 w-full sm:w-96 max-w-[calc(100vw-2rem)] bg-navy-800 border border-white/10 rounded-xl shadow-lg z-[9999] datepicker-popup">
                <div className="flex flex-col sm:flex-row">
                  {/* Quick Select Options */}
                  <div className="w-full sm:w-32 p-4 border-b sm:border-b-0 sm:border-r border-white/10">
                    <div className="space-y-2 grid grid-cols-2 sm:grid-cols-1 gap-2">
                      {quickSelectOptions.map((option, index) => {
                        const isSelected = isQuickSelectSelected(option);
                        return (
                          <button
                            key={index}
                            onClick={() => handleQuickSelect(option)}
                            className={`w-full text-left text-sm px-2 py-1 rounded-lg transition-colors cursor-pointer ${
                              isSelected
                                ? "text-white bg-[#FFFFFF]/10"
                                : "text-white hover:bg-[#FFFFFF]/10"
                            }`}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Calendar */}
                  <div className="flex-1 p-4">
                    <div className="flex justify-between items-center mb-4">
                      <button
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                        className="text-white hover:text-[#8E8E8E]"
                      >
                        <MdChevronLeft className="h-5 w-5" />
                      </button>
                      <h3 className="text-white font-semibold">
                        {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                      </h3>
                      <button
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                        className="text-white hover:text-[#8E8E8E]"
                      >
                        <MdChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                    
                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center text-white text-sm font-medium py-2">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-7 gap-1">
                      {getDaysInMonth(currentMonth).map((dayObj, index) => (
                        <button
                          key={index}
                          onClick={() => handleDateClick(dayObj.date)}
                          className={`
                            h-7 w-7 text-sm rounded
                            ${dayObj.isCurrentMonth ? 'text-white' : 'text-gray-500'}
                            ${isDateInRange(dayObj.date) ? 'bg-orangePrimary/70 text-white' : 'hover:bg-white/10'}
                          `}
                        >
                          {dayObj.day}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;

